import './i18n/config.js';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { GuestRoomPage } from './components/guest/GuestRoomPage';
import { SharedLinkPage } from './components/files/SharedLinkPage';
import { resolveThemeByDomain } from './hooks/useOrgTheme';
import './index.css';

// ── axe-core dev overlay (WCAG 2.1 AA auditing in dev mode) ─────────────────
if (import.meta.env.DEV) {
  import('@axe-core/react').then(axe => {
    axe.default(React, ReactDOM, 1000);   // reports every 1 s to console
  });
}

// Register service worker for PWA / push notification support (skip inside Tauri desktop)
const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
// Production only: in dev the SW cache served stale bundles over Vite's fresh ones
if ('serviceWorker' in navigator && !isTauri && import.meta.env.PROD) {
  // Auto-reload once when a new SW activates (clears stale SW)
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!reloading) { reloading = true; window.location.reload(); }
  });

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      // Force immediate update check against server
      await reg.update();
      // If a new worker is already waiting, activate it now
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw?.addEventListener('statechange', () => {
          if (nw.state === 'installed' && reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    } catch (err) {
      console.warn('SW registration failed:', err);
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// Resolve white-label theme by hostname before first render (non-blocking)
resolveThemeByDomain(window.location.hostname).catch(() => { /* use platform defaults */ });

// ── Route detection ─────────────────────────────────────────────────────────
// /guest/:token  → standalone guest page (no Eurion account needed)
// /meet/:roomId  → resolve room → guest token → guest page
const guestMatch = window.location.pathname.match(/^\/guest\/([A-Za-z0-9_-]+)$/);
const meetMatch = window.location.pathname.match(/^\/meet\/([0-9a-f-]+)$/i);
// /s/:token → a file share link (public, or for signed-in colleagues)
const shareMatch = window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]{32,64})$/);

/**
 * MeetRedirect — resolves /meet/:roomId to a guest token, then renders GuestRoomPage.
 */
function MeetRedirect({ roomId }: { roomId: string }) {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const apiBase = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';
    fetch(`${apiBase}/v1/guest/meet/${roomId}`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data?.token) {
          setToken(res.data.token);
        } else {
          setError(res.error?.message ?? 'Meeting room not found');
        }
      })
      .catch(() => setError('Could not connect to Eurion. Please try again.'));
  }, [roomId]);

  if (error) {
    return (
      <div className="min-h-screen bg-nord-0 flex items-center justify-center">
        <div className="bg-nord-1 rounded-xl p-8 text-center max-w-md">
          <div className="text-4xl mb-4">😔</div>
          <h1 className="text-xl font-bold text-nord-6 mb-2">Meeting Not Found</h1>
          <p className="text-nord-4/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-nord-0 flex items-center justify-center">
        <div className="text-nord-8 flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span className="text-lg">Connecting to meeting…</span>
        </div>
      </div>
    );
  }

  return <GuestRoomPage token={token} />;
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {guestMatch ? (
        <GuestRoomPage token={guestMatch[1]!} />
      ) : meetMatch ? (
        <MeetRedirect roomId={meetMatch[1]!} />
      ) : shareMatch ? (
        <SharedLinkPage token={shareMatch[1]!} />
      ) : (
        <App />
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);
